import { useEffect, useMemo, useState } from 'react'
import { Loader2, Mic } from 'lucide-react'

import api from '@/services/api'
import type { Message } from '@/types'
import { cn } from '@/utils/cn'

interface AudioMessagePlayerProps {
  message: Message
  isOutbound: boolean
}

function isInternalApiUrl(url: string) {
  return url.startsWith('/api/') || url.startsWith('/api/v1/')
}

function isPlayableUrl(url: string) {
  if (!url) return false
  if (url.startsWith('data:audio/')) return true
  if (url.startsWith('blob:')) return true
  if (url.startsWith('http://')) return true
  if (url.startsWith('https://')) return true
  if (isInternalApiUrl(url)) return true
  return false
}

function mapAudioStatus(status?: string | null): string | null {
  switch (status) {
    case 'stored':
      return 'Audio pronto'
    case 'pending_download':
      return 'Baixando audio'
    case 'remote_url':
      return 'Audio remoto'
    case 'store_failed':
      return 'Falha ao salvar'
    case 'missing_media':
      return 'Audio sem arquivo'
    case 'too_large':
      return 'Audio muito grande'
    default:
      return status ?? null
  }
}

export function AudioMessagePlayer({ message, isOutbound }: AudioMessagePlayerProps) {
  const [src, setSrc] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaUrl = message.media_url || ''
  const mimetype = message.media_mimetype || 'audio/ogg; codecs=opus'
  const statusLabel = mapAudioStatus(message.media_status)

  const label = useMemo(() => {
    if (message.media_duration_seconds) {
      return `Audio - ${message.media_duration_seconds}s`
    }
    return 'Audio'
  }, [message.media_duration_seconds])

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false

    async function loadAudio() {
      setError(null)
      setSrc(null)

      if (!mediaUrl) {
        setError(
          message.media_status === 'pending_download'
            ? 'Audio recebido. A midia ainda esta sendo preparada.'
            : 'Audio sem arquivo disponivel.',
        )
        return
      }

      if (!isPlayableUrl(mediaUrl)) {
        setError('Formato de audio nao reproduzivel.')
        return
      }

      if (isInternalApiUrl(mediaUrl)) {
        try {
          const response = await api.get(mediaUrl, {
            responseType: 'blob',
          })
          objectUrl = URL.createObjectURL(response.data)
          if (!cancelled) {
            setSrc(objectUrl)
          }
          return
        } catch {
          if (!cancelled) {
            setError('Nao foi possivel carregar o audio.')
          }
          return
        }
      }

      setSrc(mediaUrl)
    }

    void loadAudio()

    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [mediaUrl, message.media_status])

  return (
    <div
      className={cn(
        'min-w-[240px] max-w-[320px] rounded-xl',
        isOutbound ? 'text-white' : 'text-[var(--text-primary)]',
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-3 text-xs opacity-80">
        <span className="inline-flex items-center gap-2 font-medium">
          <Mic size={14} />
          {label}
        </span>
        {statusLabel && (
          <span className="rounded-full bg-black/20 px-2 py-0.5">
            {statusLabel}
          </span>
        )}
      </div>

      {src ? (
        <audio controls preload="metadata" className="w-full">
          <source src={src} type={mimetype} />
          Seu navegador nao suporta reproducao de audio.
        </audio>
      ) : (
        <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-3 text-xs opacity-90">
          <div className="flex items-center gap-2">
            <Loader2 size={14} className="shrink-0 animate-spin" />
            <span>{error || 'Carregando audio...'}</span>
          </div>
        </div>
      )}
    </div>
  )
}
